import React, { useState, useEffect } from "react";
import {
  Briefcase,
  FileText,
  CheckCircle,
  Shield,
  Clock,
  Calendar,
  Upload,
  MessageSquare,
  AlertTriangle,
  Save,
  Check,
  Plus,
  CheckSquare,
  ArrowRightCircle,
  ListTodo,
  PlayCircle,
  Send,
  Eye,
  ArrowLeft,
  Mail,
  MapPin,
  Scale,
  HelpCircle,
  Camera,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Paperclip,
} from "lucide-react";
import FormControl from "../../components/common/FormControl";
import Toast, { ToastMessage } from "../../components/common/Toast";
import Modal from "../../components/common/Modal";
import Button from "../../components/common/Button";
import { statusBadgeCss, severityBadgeCss } from "../../utils/statusVariant";

// --- Constants & Roles ---
const ROLES = {
  HEAD: "Agency Partner (Head)",
  IN_CHARGE: "Engagement Manager (In-Charge)",
  MAKER: "Senior Auditor (Maker)",
  CHECKER: "Audit Manager (Checker)",
};

// --- Mock Data ---
const MY_AGENCY = "Deloitte & Touche";
const LOGGED_IN_USER = "Jane Smith";

const PROCEDURE_TYPES = {
  CHECKLIST: "Checklists",
  QUESTIONNAIRE: "Questionnaire",
  FORM: "Form",
  RATING: "Rating System",
  WALKTHROUGH: "Process Walk-through",
  RISK_ASSESSMENT: "Risk Assessment",
  CONTROL_TESTING: "Control Testing",
  CONFIRMATIONS: "Balance Confirmations",
  PHYSICAL: "Physical Inspection",
  COMPLIANCE: "Compliance Check",
};

const INITIAL_PROCEDURES = [
  {
    id: "P-101",
    name: "Verify Bank Reconciliation",
    program: "Internal Financial Controls FY24",
    programLead: "Jane Smith",
    programEnd: "2024-06-30",
    type: PROCEDURE_TYPES.CONFIRMATIONS,
    objective:
      "To confirm the existence and accuracy of bank balances as of the reporting date.",
    summary:
      "Send confirmation letters to all banks with open accounts. Reconcile received responses with GL balances.",
    maker: "John Doe",
    checker: "Jane Smith",
    status: "Initiated",
    rolloutDate: "2024-04-01",
    dueDate: "2024-05-10",
    population: "General Ledger - Cash Accounts",
    sampleSize: "Citibank Operating Account #9988",
  },
  {
    id: "P-102",
    name: "Petty Cash Physical Count",
    program: "Internal Financial Controls FY24",
    programLead: "Jane Smith",
    programEnd: "2024-06-30",
    type: PROCEDURE_TYPES.PHYSICAL,
    objective: "To verify the physical existence of petty cash funds.",
    summary:
      "Conduct surprise counts at 4 major branch locations. Reconcile physical cash with the petty cash register.",
    maker: "Unassigned",
    checker: "Unassigned",
    status: "Initiated",
    rolloutDate: "2024-05-01",
    dueDate: "2024-05-12",
    population: "Branch Location List",
    sampleSize: "New York Downtown Branch",
  },
  {
    id: "P-103",
    name: "Vendor Invoice Sampling",
    program: "Internal Financial Controls FY24",
    programLead: "Jane Smith",
    programEnd: "2024-06-30",
    type: PROCEDURE_TYPES.CHECKLIST,
    objective:
      "To ensure all vendor invoices are properly authorized and recorded.",
    summary:
      "Select a random sample of invoices > $5k. Verify PO matching, GRN, and approval signatures.",
    maker: "John Doe",
    checker: "Jane Smith",
    status: "Submitted for Approval",
    rolloutDate: "2024-04-20",
    dueDate: "2024-05-08",
    population: "AP Sub-ledger Q1",
    sampleSize: "Inv #INV-2024-001 to #INV-2024-025",
  },
  {
    id: "P-104",
    name: "Payroll Variance Analysis",
    program: "Internal Financial Controls FY24",
    programLead: "Jane Smith",
    programEnd: "2024-06-30",
    type: PROCEDURE_TYPES.RATING,
    objective:
      "To identify and explain significant fluctuations in payroll expenses.",
    summary:
      "Compare current month payroll vs. prior month. Investigate variance > 5%. Rate the effectiveness of the review control.",
    maker: "Emily Davis",
    checker: "Jane Smith",
    status: "Submitted for Review",
    rolloutDate: "2024-04-15",
    dueDate: "2024-05-05",
    population: "Payroll Register - May 2024",
    sampleSize: "Variance Report > 5%",
  },
  {
    id: "P-105",
    name: "Fixed Asset Register Check",
    program: "Internal Financial Controls FY24",
    programLead: "Jane Smith",
    programEnd: "2024-06-30",
    type: PROCEDURE_TYPES.CONTROL_TESTING,
    objective:
      "To validate the completeness and accuracy of the Fixed Asset Register.",
    summary:
      "Test key ITGCs around the fixed asset module. Verify access controls and change management logs.",
    maker: "John Doe",
    checker: "Jane Smith",
    status: "Closed",
    rolloutDate: "2024-04-10",
    dueDate: "2024-05-01",
    population: "SAP Fixed Asset Module",
    sampleSize: "Asset #FA-4402 (Heavy Machinery)",
  },
  {
    id: "P-106",
    name: "User Access Log Review",
    program: "ITGC - Access Review Q2",
    programLead: "Mike Ross",
    programEnd: "2024-06-15",
    type: PROCEDURE_TYPES.QUESTIONNAIRE,
    objective: "To confirm that user access rights are reviewed periodically.",
    summary:
      "Interview the IT Security Manager. Obtain evidence of the last quarterly access review.",
    maker: "John Doe",
    checker: "Jane Smith",
    status: "Initiated",
    rolloutDate: "2024-05-01",
    dueDate: "2024-05-15",
    population: "Active Directory Users",
    sampleSize: "Finance Dept Users Group",
  },
  {
    id: "P-107",
    name: "Procurement Process Walkthrough",
    program: "Internal Financial Controls FY24",
    programLead: "Jane Smith",
    programEnd: "2024-06-30",
    type: PROCEDURE_TYPES.WALKTHROUGH,
    objective: "To understand the end-to-end procurement cycle.",
    summary:
      "Walkthrough from PR creation to Payment. Identify key control points.",
    maker: "John Doe",
    checker: "Jane Smith",
    status: "Initiated",
    rolloutDate: "2024-05-01",
    dueDate: "2024-05-15",
    population: "Procurement Policy SOP",
    sampleSize: "Purchase Requisition #PR-992",
  },
  {
    id: "P-108",
    name: "Entity Level Risk Assessment",
    program: "Internal Financial Controls FY24",
    programLead: "Jane Smith",
    programEnd: "2024-06-30",
    type: PROCEDURE_TYPES.RISK_ASSESSMENT,
    objective: "To assess entity-level risks impacting financial reporting.",
    summary:
      "Identify risks, assess impact/likelihood, and link to mitigating controls to determine residual risk.",
    maker: "John Doe",
    checker: "Jane Smith",
    status: "Initiated",
    rolloutDate: "2024-05-01",
    dueDate: "2024-05-15",
    population: "Enterprise Risk Register",
    sampleSize: "Financial Reporting Risks",
  },
  {
    id: "P-109",
    name: "Employee Data Update Form",
    program: "Internal Financial Controls FY24",
    programLead: "Jane Smith",
    programEnd: "2024-06-30",
    type: PROCEDURE_TYPES.FORM,
    objective: "To capture employee master data changes.",
    summary:
      "Standard form for documenting changes to employee salary or role.",
    maker: "John Doe",
    checker: "Jane Smith",
    status: "Initiated",
    rolloutDate: "2024-05-01",
    dueDate: "2024-05-15",
    population: "HR Master Data",
    sampleSize: "Employee #EMP-9928",
  },
  {
    id: "P-110",
    name: "GDPR Data Privacy Review",
    program: "ITGC - Access Review Q2",
    programLead: "Mike Ross",
    programEnd: "2024-06-15",
    type: PROCEDURE_TYPES.COMPLIANCE,
    objective: "To ensure compliance with GDPR Article 5 and 17.",
    summary:
      "Map system controls against GDPR requirements. Identify gaps in data retention and erasure.",
    maker: "Mike Ross",
    checker: "Jane Smith",
    status: "Initiated",
    rolloutDate: "2024-05-01",
    dueDate: "2024-06-01",
    population: "Customer CRM Database",
    sampleSize: "EU Customer Records",
  },
];

// --- Action Toolbar Component ---
const ActionToolbar = ({ itemId, onAction, compact = false }: any) => {
  return (
    <div className={`flex items-center ${compact ? "gap-1" : "gap-2"}`}>
      <button
        onClick={(e: any) => {
          e.stopPropagation();
          onAction("evidence", itemId);
        }}
        className="p-1.5 rounded hover:bg-orange-50 text-gray-400 hover:text-orange-600 transition-colors"
        title="Attach Evidence"
      >
        <Paperclip size={14} />
      </button>
      <button
        onClick={(e: any) => {
          e.stopPropagation();
          onAction("approval", itemId);
        }}
        className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
        title="Approval Note"
      >
        <CheckCircle size={14} />
      </button>
      <button
        onClick={(e: any) => {
          e.stopPropagation();
          onAction("review", itemId);
        }}
        className="p-1.5 rounded hover:bg-purple-50 text-gray-400 hover:text-purple-600 transition-colors"
        title="Reviewer Note"
      >
        <Eye size={14} />
      </button>
      <button
        onClick={(e: any) => {
          e.stopPropagation();
          onAction("escalation", itemId);
        }}
        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
        title="Escalations"
      >
        <AlertTriangle size={14} />
      </button>
      <button
        onClick={(e: any) => {
          e.stopPropagation();
          onAction("comments", itemId);
        }}
        className="p-1.5 rounded hover:bg-orange-50 text-gray-400 hover:text-orange-600 transition-colors"
        title="Comments"
      >
        <MessageSquare size={14} />
      </button>
    </div>
  );
};

// --- Universal Action Modal ---
const UniversalActionModal = ({ isOpen, onClose, type, itemId }: any) => {
  const getTitle = () => {
    switch (type) {
      case "evidence":
        return "Attach Evidence";
      case "approval":
        return "Approval Note";
      case "review":
        return "Reviewer Note";
      case "escalation":
        return "Manage Escalations";
      case "comments":
        return "Comments & Queries";
      default:
        return "Action";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "evidence":
        return <Paperclip size={16} style={{ color: "var(--brand)" }} />;
      case "approval":
        return <CheckCircle size={16} className="text-green-500" />;
      case "review":
        return <Eye size={16} className="text-purple-500" />;
      case "escalation":
        return <AlertTriangle size={16} className="text-red-500" />;
      case "comments":
        return <MessageSquare size={16} style={{ color: "var(--brand)" }} />;
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <span className="flex items-center gap-2">
          {getIcon()} {getTitle()}
          <span
            style={{ color: "#9ca3af", fontWeight: 400, fontSize: "0.8rem" }}
          >
            #{itemId}
          </span>
        </span>
      </Modal.Header>

      <Modal.Body>
        {type === "evidence" && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors cursor-pointer">
              <Upload size={24} className="text-gray-400 mb-2" />
              <p className="text-xs font-bold text-gray-600">
                Click or Drag to upload files
              </p>
            </div>
            <div>
              <h5 className="text-xs font-bold text-gray-500 mb-2 uppercase">
                Attached Files
              </h5>
              <div className="bg-gray-50 rounded p-2 text-xs text-gray-500 italic text-center">
                No files attached yet.
              </div>
            </div>
          </div>
        )}

        {(type === "approval" || type === "review") && (
          <div className="space-y-3">
            <label className="text-xs font-bold text-gray-600">
              {type === "approval"
                ? "Approver Remarks"
                : "Reviewer Observations"}
            </label>
            <FormControl.Textarea
              placeholder="Type your note here..."
              rows={5}
            />
            <div className="flex justify-end">
              <span className="text-[10px] text-gray-400">
                Last updated by Jane Smith on 2024-05-02
              </span>
            </div>
          </div>
        )}

        {type === "escalation" && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-100 p-3 rounded-lg">
              <div className="flex justify-between items-start">
                <span className="text-xs font-bold text-red-800">
                  ESC-001: Missing Documentation
                </span>
                <span className={statusBadgeCss("Open", "xs")}>
                  Open
                </span>
              </div>
              <p className="text-xs text-red-700 mt-1">
                Evidence for sample #3 was not provided by the deadline.
              </p>
            </div>
            <Button variant="fill" icon={Plus} onClick={() => {}}>
              Raise Escalation
            </Button>
          </div>
        )}

        {type === "comments" && (
          <div className="flex flex-col h-64">
            <div className="flex-1 overflow-y-auto space-y-3 mb-3 pr-1">
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600">
                  JD
                </div>
                <div className="bg-gray-100 p-2 rounded-lg rounded-tl-none text-xs text-gray-700 max-w-[85%]">
                  Please check the date format on line 4.
                </div>
              </div>
              <div className="flex gap-2 flex-row-reverse">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-600">
                  JS
                </div>
                <div className="bg-blue-50 p-2 rounded-lg rounded-tr-none text-xs text-blue-800 max-w-[85%]">
                  Fixed it. Can you review?
                </div>
              </div>
            </div>
            <div className="mt-auto pt-3 border-t border-gray-100 flex gap-2">
              <FormControl.Input
                type="text"
                placeholder="Type a comment..."
                size="sm"
              />
              <button className="p-2 text-white rounded-lg" style={{ backgroundColor: "var(--brand)" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--brand-hover)"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "var(--brand)"}
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        )}
      </Modal.Body>

      {type !== "comments" && (
        <Modal.Footer>
          <Button variant="fill" icon={Save} onClick={onClose}>
            Submit
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

// --- PROCEDURE VIEW MODULES ---

// 1. Checklist Module
const ChecklistView = ({ setHeaderStatus, onAction }: any) => {
  const [questions, setQuestions] = useState([
    {
      id: 1,
      q: "Verify invoice date matches PO date",
      answer: "yes",
      comment: "Matches perfectly.",
    },
    {
      id: 2,
      q: "Are appropriate authorization signatures present?",
      answer: "no",
      comment: "Missing Manager signature.",
    },
    {
      id: 3,
      q: "Validate vendor tax ID against master data",
      answer: "yes",
      comment: "",
    },
    {
      id: 4,
      q: "Confirm GL coding is correct",
      answer: "na",
      comment: "Auto-coded.",
    },
  ]);

  const setAnswer = (id: any, val: any) =>
    setQuestions(
      questions.map((q) => (q.id === id ? { ...q, answer: val } : q)),
    );

  useEffect(() => {
    const completed = questions.filter((q) =>
      ["yes", "no", "na"].includes(q.answer),
    ).length;
    setHeaderStatus(
      <span className="status-badge xs warning flex items-center gap-2 whitespace-nowrap">
        <CheckSquare size={14} /> {completed} / {questions.length} Checked
      </span>,
    );
  }, [questions, setHeaderStatus]);

  return (
    <div className="space-y-3">
      {questions.map((q, idx) => (
        <div
          key={q.id}
          className="bg-white p-3 rounded border border-gray-200 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <span className="text-gray-400 text-xs pt-1">
              {" "}
              {idx + 1}.
            </span>
            <div className="flex-1">
              <div className="flex justify-between items-start mb-2">
                <p className="font-medium text-gray-800 text-sm"> {q.q} </p>
                <ActionToolbar itemId={q.id} onAction={onAction} compact />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex gap-2">
                  {["yes", "no", "na"].map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer border transition-all ${q.answer === opt ? (opt === "yes" ? "bg-green-100 border-green-300 text-green-700" : opt === "no" ? "bg-red-100 border-red-300 text-red-700" : "bg-gray-100 border-gray-300 text-gray-700") : "hover:bg-gray-50 border-gray-200"}`}
                    >
                      <input
                        type="radio"
                        name={`q-${q.id}`}
                        checked={q.answer === opt}
                        onChange={() => setAnswer(q.id, opt)}
                        className="hidden"
                      />
                      <div
                        className={`w-3 h-3 rounded-full border flex items-center justify-center ${q.answer === opt ? "border-current" : "border-gray-300"}`}
                      >
                        {q.answer === opt && (
                          <div className="w-1.5 h-1.5 rounded-full bg-current" />
                        )}
                      </div>
                      <span className="text-[10px] font-bold uppercase">
                        {" "}
                        {opt === "na" ? "N/A" : opt}
                      </span>
                    </label>
                  ))}
                </div>
                <FormControl.Input
                  type="text"
                  placeholder="Brief remarks..."
                  defaultValue={q.comment}
                  size="sm"
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// 2. Questionnaire Module
const QuestionnaireView = ({ setHeaderStatus, onAction }: any) => {
  const [items, setItems] = useState([
    {
      id: 1,
      text: "Describe the process for investigating payroll variances.",
      answer:
        "Variances > 5% are flagged by the system. HR Manager reviews these weekly and documents reasons in the portal.",
    },
    {
      id: 2,
      text: "Explain how vendor due diligence is performed prior to onboarding.",
      answer: "",
    },
    {
      id: 3,
      text: "What measures are in place to prevent duplicate payments?",
      answer: "",
    },
  ]);

  useEffect(() => {
    const answered = items.filter((i) => i.answer.length > 0).length;
    setHeaderStatus(
      <span className="status-badge xs info flex items-center gap-2 whitespace-nowrap">
        <MessageSquare size={14} /> {answered} / {items.length} Answered
      </span>,
    );
  }, [items, setHeaderStatus]);

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div
          key={item.id}
          className="bg-white p-4 rounded border border-gray-200 shadow-sm flex flex-col gap-2"
        >
          <div className="flex justify-between items-start">
            <div className="flex gap-2 items-start">
              <span className="bg-gray-100 text-gray-500 font-bold text-[10px] px-1.5 py-0.5 rounded flex-shrink-0">
                {" "}
                Q{idx + 1}{" "}
              </span>
              <p className="text-sm font-semibold text-gray-800">
                {" "}
                {item.text}{" "}
              </p>
            </div>
            <ActionToolbar itemId={item.id} onAction={onAction} compact />
          </div>
          <FormControl.Textarea
            placeholder="Type detailed answer..."
            defaultValue={item.answer}
            rows={3}
          />
        </div>
      ))}
    </div>
  );
};

// 3. Form Module
const FormView = ({ setHeaderStatus, onAction }: any) => {
  useEffect(() => {
    setHeaderStatus(
      <span className="text-xs font-bold text-gray-500 whitespace-nowrap">
        {" "}
        Draft Mode{" "}
      </span>,
    );
  }, [setHeaderStatus]);

  return (
    <div className="bg-white p-6 rounded border border-gray-200 shadow-sm max-w-3xl mx-auto relative">
      <div className="absolute top-4 right-4">
        <ActionToolbar itemId="FORM-MAIN" onAction={onAction} />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4 mt-2">
        <FormControl.Input
          label="Employee ID"
          type="text"
          defaultValue="EMP-9928"
          size="sm"
        />
        <FormControl.Input
          label="Full Name"
          type="text"
          defaultValue="Jonathan Doe"
          size="sm"
        />
      </div>
      <div className="mb-4">
        <FormControl.Select label="Department" size="sm">
          <option>Finance</option>
          <option>HR</option>
          <option>IT</option>
        </FormControl.Select>
      </div>
      <div className="mb-4">
        <FormControl.Textarea
          label="Justification"
          placeholder="Enter details..."
          rows={4}
        />
      </div>
      <div className="pt-2 flex justify-end">
        <Button variant="fill" icon={Save}>
          Submit
        </Button>
      </div>
    </div>
  );
};

// 4. Rating System Module
const RatingSystemView = ({ setHeaderStatus, onAction }: any) => {
  const [questions, setQuestions] = useState([
    {
      id: 1,
      text: "Control Design Effectiveness",
      maxScore: 10,
      selectedScore: 0,
      options: [
        { label: "Select Rating...", value: 0, isPlaceholder: true },
        { label: "Ineffective - Control is missing or flawed (0)", value: 0 },
        { label: "Needs Improvement - Minor gaps exist (5)", value: 5 },
        { label: "Effective - Well designed (10)", value: 10 },
      ],
    },
    {
      id: 2,
      text: "Operating Effectiveness",
      maxScore: 20,
      selectedScore: 0,
      options: [
        { label: "Select Rating...", value: 0, isPlaceholder: true },
        { label: "Fail - Frequent exceptions (0)", value: 0 },
        { label: "Qualified Pass - Isolated exceptions (10)", value: 10 },
        { label: "Pass - No exceptions (20)", value: 20 },
      ],
    },
    {
      id: 3,
      text: "Documentation Quality",
      maxScore: 5,
      selectedScore: 0,
      options: [
        { label: "Select Rating...", value: 0, isPlaceholder: true },
        { label: "Poor - None available (0)", value: 0 },
        { label: "Average - Incomplete (3)", value: 3 },
        { label: "Good - Comprehensive (5)", value: 5 },
      ],
    },
  ]);

  const handleScoreChange = (id: any, newScore: any) => {
    setQuestions(
      questions.map((q) =>
        q.id === id ? { ...q, selectedScore: parseInt(newScore) } : q,
      ),
    );
  };

  useEffect(() => {
    const obtained = questions.reduce((a, b) => a + b.selectedScore, 0);
    const total = questions.reduce((a, b) => a + b.maxScore, 0);
    setHeaderStatus(
      <span className="status-badge xs warning flex items-center gap-2 whitespace-nowrap">
        <Scale size={14} /> Score: {obtained} / {total}
      </span>,
    );
  }, [questions, setHeaderStatus]);

  return (
    <div className="grid grid-cols-4 gap-4">
      {questions.map((q, idx) => (
        <div
          key={q.id}
          className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col gap-3 group"
        >
          {/* Header row: serial + title + action icons */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2">
              <span className="font-bold text-gray-400 text-xs pt-0.5 shrink-0">
                {idx + 1}.
              </span>
              <p className="font-semibold text-gray-800 text-sm leading-snug">
                {q.text}
              </p>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
              <ActionToolbar itemId={q.id} onAction={onAction} compact />
            </div>
          </div>

          {/* Dropdown */}
          <FormControl.Select
            value={q.selectedScore}
            onChange={(e: any) => handleScoreChange(q.id, e.target.value)}
            size="sm"
          >
            {q.options.map((opt, i) => (
              <option key={i} value={opt.value} disabled={opt.isPlaceholder}>
                {opt.label}
              </option>
            ))}
          </FormControl.Select>

          {/* Score */}
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-orange-700">
              {q.selectedScore}
            </span>
            <span className="text-sm text-gray-400 font-medium">
              / {q.maxScore}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// 6. Process Walk-through Module
const WalkthroughView = ({ setHeaderStatus, onAction }: any) => {
  const [steps, setSteps] = useState([
    {
      id: 1,
      title: "Requisition",
      desc: "User creates a purchase requisition in SAP.",
      hasScreenshot: true,
    },
    {
      id: 2,
      title: "Approval",
      desc: "Department head approves requisition via email notification.",
      hasScreenshot: false,
    },
    {
      id: 3,
      title: "PO Generation",
      desc: "Procurement team converts PR to PO.",
      hasScreenshot: false,
    },
  ]);
  const [activeStepId, setActiveStepId] = useState(1);

  const activeStep = steps.find((s) => s.id === activeStepId) ?? steps[0];

  useEffect(() => {
    setHeaderStatus(
      <span className="text-xs font-bold text-gray-500 whitespace-nowrap">
        {steps.length} Steps Defined
      </span>,
    );
  }, [setHeaderStatus, steps]);

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Left nav card ───────────────────────────────────────────── */}
      <div className="netbody-card side-nav-card" style={{ width: 220 }}>
        <div className="side-nav-card-heading">Walk-through Steps</div>
        <nav aria-label="Steps navigation">
          {steps.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => setActiveStepId(step.id)}
              className={`side-panel-menu-item${activeStepId === step.id ? " side-panel-menu-item--active" : ""}`}
            >
              <span className="shrink-0 text-[10px] font-bold opacity-60">
                {idx + 1}.
              </span>
              {step.title}
            </button>
          ))}
          <div className="side-panel-menu-divider" />
          <button
            className="side-panel-menu-item"
            onClick={() => {
              const next = steps.length + 1;
              const newStep = {
                id: next,
                title: `Step ${next}`,
                desc: "",
                hasScreenshot: false,
              };
              setSteps([...steps, newStep]);
              setActiveStepId(next);
            }}
          >
            <Plus size={13} />
            Add Step
          </button>
        </nav>
      </div>

      {/* ── Right content card ──────────────────────────────────────── */}
      <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">
        {/* Step header */}
        <div
          className="netbody-header netbody-header--center"
          style={{ padding: "10px 16px" }}
        >
          <div className="flex flex-col min-w-0">
            <h4 className="font-semibold text-gray-800 text-sm leading-snug">
              Step {steps.findIndex((s) => s.id === activeStepId) + 1}:{" "}
              {activeStep.title}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {activeStep.desc || "No description added."}
            </p>
          </div>
          <ActionToolbar
            itemId={`STEP-${activeStep.id}`}
            onAction={onAction}
            compact
          />
        </div>

        {/* Step body */}
        <div className="netbody-content">
          <div className="flex flex-col gap-4 p-4">
            {/* Screenshot upload zone */}
            <div className="flex-1 min-h-[200px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 gap-2 hover:border-orange-300 hover:bg-orange-50/30 transition-colors cursor-pointer">
              <Camera size={28} className="opacity-40" />
              <span className="text-xs font-medium">
                No screenshot uploaded
              </span>
              <button className="text-orange-600 text-xs font-bold hover:underline">
                Browse
              </button>
            </div>

            {/* Observation textarea */}
            <FormControl.Textarea
              label="Auditor Observation"
              placeholder="Enter your observation for this step..."
              rows={4}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// 7. Risk Assessment Module (Refactored)
const RiskAssessmentView = ({ setHeaderStatus, onAction }: any) => {
  const [risks, setRisks] = useState([
    {
      id: "R-01",
      desc: "Unauthorized financial data access",
      likelihood: 3,
      severity: 3,
      controlExistence: 3, // Manual entry for parent
      controlEffectiveness: 3, // Manual entry for parent
      controls: [
        {
          id: "C-01",
          name: "MFA on all financial systems",
          effectiveness: "High",
          existence: "Always",
          attributes: [
            {
              id: "CA1",
              text: "MFA Enforced for remote access",
              status: "pass",
            },
            { id: "CA2", text: "SMS fallback enabled", status: "fail" },
          ],
          comment: "Configured correctly.",
        },
        {
          id: "C-02",
          name: "Quarterly Access Review",
          effectiveness: "Medium",
          existence: "Often",
          attributes: [
            { id: "CA1", text: "Review sign-off obtained", status: "pass" },
          ],
          comment: "Evidence missing for Q1.",
        },
      ],
    },
    {
      id: "R-02",
      desc: "Duplicate vendor payments",
      likelihood: 2,
      severity: 2,
      controlExistence: 2,
      controlEffectiveness: 2,
      controls: [
        {
          id: "C-03",
          name: "System duplicate check",
          effectiveness: "High",
          existence: "Always",
          attributes: [
            { id: "CA1", text: "Exact match logic active", status: "pass" },
          ],
          comment: "",
        },
      ],
    },
  ]);

  const [expandedRiskId, setExpandedRiskId] = useState<any>(null);
  const [activeControlTest, setActiveControlTest] = useState<any>(null); // { riskId, controlId }

  // Helper to calculate risk labels
  const getRiskLabel = (score: any) => {
    if (score >= 6) return { label: "High" };
    if (score >= 3) return { label: "Medium" };
    return { label: "Low" };
  };

  const getControlRiskLabel = (existence: any, effectiveness: any) => {
    const strength = existence * effectiveness;
    if (strength >= 6) return { label: "Low" };
    if (strength >= 3) return { label: "Medium" };
    return { label: "High" };
  };

  const updateRisk = (id: any, field: any, value: any) => {
    setRisks(
      risks.map((r) => (r.id === id ? { ...r, [field]: parseInt(value) } : r)),
    );
  };

  const toggleAttribute = (attrId: any) => {
    if (!activeControlTest) return;
    const { riskId, controlId } = activeControlTest;

    setRisks(
      risks.map((r) => {
        if (r.id !== riskId) return r;
        const updatedControls = r.controls.map((c) => {
          if (c.id !== controlId) return c;
          const updatedAttrs = c.attributes.map((a) =>
            a.id === attrId
              ? { ...a, status: a.status === "pass" ? "fail" : "pass" }
              : a,
          );
          return { ...c, attributes: updatedAttrs };
        });
        return { ...r, controls: updatedControls };
      }),
    );
  };

  const updateActiveControlField = (field: any, value: any) => {
    if (!activeControlTest) return;
    const { riskId, controlId } = activeControlTest;
    setRisks(
      risks.map((r) => {
        if (r.id !== riskId) return r;
        const updatedControls = r.controls.map((c) =>
          c.id === controlId ? { ...c, [field]: value } : c,
        );
        return { ...r, controls: updatedControls };
      }),
    );
  };

  // Dynamic Header Update
  useEffect(() => {
    const totalResidualScore = risks.reduce((acc, r) => {
      const inherent = r.likelihood * r.severity;
      const controlStrength = r.controlExistence * r.controlEffectiveness;
      return acc + Math.max(1, inherent - controlStrength * 0.5);
    }, 0);

    const avg = totalResidualScore / risks.length;
    const label = getRiskLabel(avg);

    setHeaderStatus(
      <span
        className={`flex items-center gap-2 whitespace-nowrap ${severityBadgeCss(label.label)}`}
      >
        <AlertTriangle size={14} /> Residual Risk: {label.label}
      </span>,
    );
  }, [setHeaderStatus, risks]);

  // Modal Content Renderer
  const renderModal = () => {
    if (!activeControlTest) return null;
    const risk = risks.find((r) => r.id === activeControlTest.riskId);
    if (!risk) return null;
    const ctrl = risk.controls.find(
      (c) => c.id === activeControlTest.controlId,
    );
    if (!ctrl) return null;

    return (
      <Modal
        isOpen={!!activeControlTest}
        onClose={() => setActiveControlTest(null)}
        size="lg"
      >
        <Modal.Header>
          <span className="flex items-center gap-2">
            <Shield size={16} style={{ color: "var(--brand)" }} /> Test Control:{" "}
            {ctrl.name}
          </span>
        </Modal.Header>

        <Modal.Body>
          <p className="text-xs text-gray-500 mb-5">Risk: {risk.desc}</p>
          <div className="space-y-6">
            {/* Dropdowns */}
            <div className="grid grid-cols-2 gap-4 bg-orange-50 p-4 rounded-lg border border-orange-100">
              <FormControl.Select
                label="Effectiveness"
                value={ctrl.effectiveness}
                onChange={(e: any) =>
                  updateActiveControlField("effectiveness", e.target.value)
                }
                size="sm"
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </FormControl.Select>
              <FormControl.Select
                label="Existence"
                value={ctrl.existence}
                onChange={(e: any) =>
                  updateActiveControlField("existence", e.target.value)
                }
                size="sm"
              >
                <option value="Always">Always</option>
                <option value="Often">Often</option>
                <option value="Never">Never</option>
              </FormControl.Select>
            </div>

            {/* Attributes */}
            <div>
              <h5 className="text-xs font-bold text-gray-700 uppercase mb-2 flex items-center gap-2">
                <ListTodo size={14} /> Test Attributes
              </h5>
              <div className="space-y-2 border rounded-lg overflow-hidden">
                {ctrl.attributes && ctrl.attributes.length > 0 ? (
                  ctrl.attributes.map((attr) => (
                    <div
                      key={attr.id}
                      className="flex items-center justify-between p-3 bg-white border-b last:border-0 border-gray-100"
                    >
                      <span className="text-sm text-gray-700 font-medium">
                        {attr.text}
                      </span>
                      <div className="flex bg-gray-100 rounded p-0.5">
                        <button
                          onClick={() => {
                            if (attr.status !== "pass")
                              toggleAttribute(attr.id);
                          }}
                          className={`px-3 py-1 rounded text-[10px] font-bold transition-all ${attr.status === "pass" ? "bg-white text-green-700 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
                        >
                          PASS
                        </button>
                        <button
                          onClick={() => {
                            if (attr.status !== "fail")
                              toggleAttribute(attr.id);
                          }}
                          className={`px-3 py-1 rounded text-[10px] font-bold transition-all ${attr.status === "fail" ? "bg-white text-red-600 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
                        >
                          FAIL
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-xs text-gray-400 italic">
                    No specific attributes defined.
                  </div>
                )}
              </div>
            </div>

            {/* Comment */}
            <FormControl.Textarea
              label="Auditor Remarks"
              placeholder="Enter observations..."
              value={ctrl.comment || ""}
              onChange={(e: any) =>
                updateActiveControlField("comment", e.target.value)
              }
              rows={3}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="update"
            icon={Check}
            onClick={() => setActiveControlTest(null)}
          >
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className="space-y-4">
      {risks.map((risk) => {
        const inherentScore = risk.likelihood * risk.severity;
        const inherentLabel = getRiskLabel(inherentScore);
        const controlStrength =
          risk.controlExistence * risk.controlEffectiveness;
        const controlRiskLabel = getControlRiskLabel(
          risk.controlExistence,
          risk.controlEffectiveness,
        );
        const residualScore = Math.max(
          1,
          inherentScore - controlStrength * 0.8,
        );
        const residualLabel = getRiskLabel(residualScore);
        const isExpanded = expandedRiskId === risk.id;

        const borderAccent =
          residualLabel.label === "High"
            ? "border-l-red-500"
            : residualLabel.label === "Medium"
              ? "border-l-amber-400"
              : "border-l-green-500";

        return (
          <div
            key={risk.id}
            className={`bg-white border border-gray-200 border-l-4 ${borderAccent} rounded-lg overflow-hidden shadow-sm`}
          >
            {/* ── Card Header: ID · desc · actions ──────────────────── */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-2 min-w-0">
                <span className="shrink-0 text-[10px] font-bold bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                  {risk.id}
                </span>
                <span className="shrink-0 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Risk Scenario
                </span>
                <span className="text-gray-300 shrink-0">·</span>
                <h4 className="font-semibold text-gray-800 text-sm truncate">
                  {risk.desc}
                </h4>
              </div>
              <div className="shrink-0 ml-4">
                <ActionToolbar itemId={risk.id} onAction={onAction} compact />
              </div>
            </div>

            {/* ── Scoring + Risk Flow ────────────────────────────────── */}
            <div className="flex flex-wrap items-end gap-6 px-4 py-4">
              {/* Inherent Risk inputs */}
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">
                  Inherent Risk
                </span>
                <div className="flex gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] text-gray-400">
                      Likelihood
                    </span>
                    <FormControl.Select
                      value={risk.likelihood}
                      onChange={(e: any) =>
                        updateRisk(risk.id, "likelihood", e.target.value)
                      }
                      size="sm"
                    >
                      <option value="3">High (3)</option>
                      <option value="2">Med (2)</option>
                      <option value="1">Low (1)</option>
                    </FormControl.Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] text-gray-400">Severity</span>
                    <FormControl.Select
                      value={risk.severity}
                      onChange={(e: any) =>
                        updateRisk(risk.id, "severity", e.target.value)
                      }
                      size="sm"
                    >
                      <option value="3">High (3)</option>
                      <option value="2">Med (2)</option>
                      <option value="1">Low (1)</option>
                    </FormControl.Select>
                  </div>
                </div>
              </div>

              {/* Divider arrow */}
              <ArrowRightCircle
                size={18}
                className="text-gray-300 mb-1 shrink-0"
              />

              {/* Control strength inputs */}
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">
                  Control Strength
                </span>
                <div className="flex gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] text-gray-400">Existence</span>
                    <FormControl.Select
                      value={risk.controlExistence}
                      onChange={(e: any) =>
                        updateRisk(risk.id, "controlExistence", e.target.value)
                      }
                      size="sm"
                    >
                      <option value="3">Always (3)</option>
                      <option value="2">Often (2)</option>
                      <option value="1">Never (1)</option>
                    </FormControl.Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] text-gray-400">
                      Effectiveness
                    </span>
                    <FormControl.Select
                      value={risk.controlEffectiveness}
                      onChange={(e: any) =>
                        updateRisk(
                          risk.id,
                          "controlEffectiveness",
                          e.target.value,
                        )
                      }
                      size="sm"
                    >
                      <option value="3">High (3)</option>
                      <option value="2">Med (2)</option>
                      <option value="1">Low (1)</option>
                    </FormControl.Select>
                  </div>
                </div>
              </div>

              {/* Divider arrow */}
              <ArrowRightCircle
                size={18}
                className="text-gray-300 mb-1 shrink-0"
              />

              {/* Risk flow result badges */}
              <div className="flex items-center gap-1 ml-auto">
                {[
                  { label: "Inherent", badge: inherentLabel },
                  { label: "Control", badge: controlRiskLabel },
                  { label: "Residual", badge: residualLabel },
                ].map(({ label, badge }, i, arr) => (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center gap-1 px-3">
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">
                        {label}
                      </span>
                      <span className={severityBadgeCss(badge.label)}>
                        {badge.label}
                      </span>
                    </div>
                    {i < arr.length - 1 && (
                      <ArrowRightCircle size={14} className="text-gray-300" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* ── Associated Controls toggle ─────────────────────────── */}
            <div className="border-t border-gray-100">
              <button
                onClick={() => setExpandedRiskId(isExpanded ? null : risk.id)}
                className="w-full flex items-center justify-center gap-2 py-2 text-[10px] font-bold text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors uppercase tracking-wide"
              >
                {isExpanded ? (
                  <ChevronUp size={12} />
                ) : (
                  <ChevronDown size={12} />
                )}
                {isExpanded
                  ? "Hide Associated Controls"
                  : `View Associated Controls (${risk.controls.length})`}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4">
                  <div className="rounded-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 border-b border-gray-100 flex items-center gap-1">
                      <Shield size={11} className="text-gray-400" />
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                        Controls Inventory
                      </span>
                    </div>
                    {risk.controls.map((c, i) => (
                      <div
                        key={i}
                        onClick={() =>
                          setActiveControlTest({
                            riskId: risk.id,
                            controlId: c.id,
                          })
                        }
                        className="flex items-center justify-between px-3 py-2.5 bg-white border-b last:border-0 border-gray-50 hover:bg-orange-50 cursor-pointer transition-colors group"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <PlayCircle
                            size={14}
                            className="text-orange-400 group-hover:text-orange-600 shrink-0"
                          />
                          <span className="text-xs font-medium text-gray-700 group-hover:text-orange-700 truncate">
                            <span className="font-bold text-gray-500 mr-1">
                              {c.id}:
                            </span>
                            {c.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-4">
                          <span
                            className={`status-badge xs ${c.effectiveness === "High" ? "success" : c.effectiveness === "Medium" ? "warning" : "danger"}`}
                          >
                            Eff: {c.effectiveness}
                          </span>
                          <span
                            className={`status-badge xs ${c.existence === "Always" ? "success" : c.existence === "Often" ? "warning" : "danger"}`}
                          >
                            Ext: {c.existence}
                          </span>
                          <ChevronDown
                            size={12}
                            className="text-gray-300 -rotate-90"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {renderModal()}
    </div>
  );
};

// 8. Control Testing Module (GENERIC & NESTED)
const ControlTestingView = ({ setHeaderStatus, onAction }: any) => {
  // Generic State Structure
  const [controls, setControls] = useState([
    {
      id: "C-01",
      name: "User Access Revocation",
      effectiveness: "High", // Low, Medium, High
      existence: "Always", // Always, Often, Never
      attributes: [
        { id: "A1", text: "Request approved by manager", status: "pass" },
        { id: "A2", text: "Access removed within 24hrs", status: "fail" },
        { id: "A3", text: "Ticket closed with evidence", status: "pass" },
      ],
    },
    {
      id: "C-02",
      name: "Password Complexity Check",
      effectiveness: "Medium",
      existence: "Often",
      attributes: [
        { id: "A1", text: "Minimum 8 characters enforced", status: "pass" },
        { id: "A2", text: "Special character required", status: "pass" },
      ],
    },
  ]);

  // Scoring Logic Helpers
  const getEffScore = (val: any) =>
    val === "High" ? 10 : val === "Medium" ? 5 : 0;
  const getExistScore = (val: any) =>
    val === "Always" ? 10 : val === "Often" ? 5 : 0;

  // Update Header with Total Score
  useEffect(() => {
    const totalScore = controls.reduce(
      (acc, c) =>
        acc + getEffScore(c.effectiveness) + getExistScore(c.existence),
      0,
    );
    const maxPossible = controls.length * 20; // 10 for Eff + 10 for Exist per control

    setHeaderStatus(
      <span className="status-badge xs info flex items-center gap-2 whitespace-nowrap">
        <Shield size={14} /> Control Score: {totalScore} / {maxPossible}
      </span>,
    );
  }, [controls, setHeaderStatus]);

  const updateControl = (id: any, field: any, value: any) => {
    setControls(
      controls.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );
  };

  const toggleAttribute = (ctrlId: any, attrId: any) => {
    setControls(
      controls.map((c) => {
        if (c.id !== ctrlId) return c;
        const updatedAttrs = c.attributes.map((a) =>
          a.id === attrId
            ? { ...a, status: a.status === "pass" ? "fail" : "pass" }
            : a,
        );
        return { ...c, attributes: updatedAttrs };
      }),
    );
  };

  return (
    <div className="space-y-4">
      {controls.map((ctrl) => {
        const score =
          getEffScore(ctrl.effectiveness) + getExistScore(ctrl.existence);
        const allPass = ctrl.attributes.every((a) => a.status === "pass");
        const failCount = ctrl.attributes.filter(
          (a) => a.status === "fail",
        ).length;
        const borderColor = allPass ? "border-l-green-500" : "border-l-red-400";

        return (
          <div
            key={ctrl.id}
            className={`bg-white border border-gray-200 border-l-4 ${borderColor} rounded-lg overflow-hidden shadow-sm`}
          >
            {/* ── Card Header ─────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
              {/* Left: ID badge + name */}
              <div className="flex items-center gap-2 min-w-0">
                <span className="shrink-0 text-[10px] font-bold bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                  {ctrl.id}
                </span>
                <h4 className="font-semibold text-gray-800 text-sm truncate">
                  {ctrl.name}
                </h4>
              </div>

              {/* Right: score pill + action toolbar */}
              <div className="flex items-center gap-3 shrink-0 ml-4">
                <span
                  className={`status-badge xs ${
                    score >= 15
                      ? "success"
                      : score >= 8
                        ? "warning"
                        : "danger"
                  }`}
                >
                  Score: {score} / 20
                </span>
                <ActionToolbar itemId={ctrl.id} onAction={onAction} compact />
              </div>
            </div>

            {/* ── Scoring Dropdowns ───────────────────────────────────── */}
            <div className="flex items-end gap-6 px-4 py-3 border-b border-gray-100 bg-white">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Effectiveness
                </span>
                <FormControl.Select
                  value={ctrl.effectiveness}
                  onChange={(e: any) =>
                    updateControl(ctrl.id, "effectiveness", e.target.value)
                  }
                  size="sm"
                >
                  <option value="High">High (10)</option>
                  <option value="Medium">Medium (5)</option>
                  <option value="Low">Low (0)</option>
                </FormControl.Select>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Existence
                </span>
                <FormControl.Select
                  value={ctrl.existence}
                  onChange={(e: any) =>
                    updateControl(ctrl.id, "existence", e.target.value)
                  }
                  size="sm"
                >
                  <option value="Always">Always (10)</option>
                  <option value="Often">Often (5)</option>
                  <option value="Never">Never (0)</option>
                </FormControl.Select>
              </div>

              {/* Attribute summary chips */}
              <div className="flex items-center gap-2 ml-auto pb-0.5">
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                  {ctrl.attributes.length - failCount} Pass
                </span>
                {failCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                    {failCount} Fail
                  </span>
                )}
              </div>
            </div>

            {/* ── Attributes List ─────────────────────────────────────── */}
            <div className="px-4 py-3">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <ListTodo size={11} /> Control Attributes
              </p>
              <div className="divide-y divide-gray-50">
                {ctrl.attributes.map((attr, aIdx) => (
                  <div
                    key={attr.id}
                    className="flex items-center justify-between py-2.5 group"
                  >
                    {/* Attribute label */}
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-[10px] text-gray-400 font-medium w-5 shrink-0">
                        {aIdx + 1}.
                      </span>
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${attr.status === "pass" ? "bg-green-500" : "bg-red-400"}`}
                      />
                      <span
                        className={`text-sm truncate ${attr.status === "pass" ? "text-gray-700" : "text-red-600 font-medium"}`}
                      >
                        {attr.text}
                      </span>
                    </div>

                    {/* PASS / FAIL segmented toggle */}
                    <div className="flex shrink-0 ml-4 rounded-md overflow-hidden border border-gray-200 text-[10px] font-bold">
                      <button
                        onClick={() => {
                          if (attr.status !== "pass")
                            toggleAttribute(ctrl.id, attr.id);
                        }}
                        className={`px-4 py-1.5 transition-all ${
                          attr.status === "pass"
                            ? "bg-green-600 text-white"
                            : "bg-white text-gray-400 hover:bg-green-50 hover:text-green-600"
                        }`}
                      >
                        PASS
                      </button>
                      <button
                        onClick={() => {
                          if (attr.status !== "fail")
                            toggleAttribute(ctrl.id, attr.id);
                        }}
                        className={`px-4 py-1.5 border-l border-gray-200 transition-all ${
                          attr.status === "fail"
                            ? "bg-red-500 text-white"
                            : "bg-white text-gray-400 hover:bg-red-50 hover:text-red-600"
                        }`}
                      >
                        FAIL
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// 9. Balance Confirmations Module
const BalanceConfirmationsView = ({ setHeaderStatus, onAction }: any) => {
  useEffect(() => {
    setHeaderStatus(
      <span className="status-badge xs success">
        <CheckCircle size={14} /> 95 % Response Rate
      </span>,
    );
  }, [setHeaderStatus]);

  const STATS = [
    {
      label: "Sent",
      value: "120",
      icon: Mail,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-500",
      valColor: "text-blue-600",
      topBorder: "border-t-blue-400",
    },
    {
      label: "Received",
      value: "95",
      icon: CheckCircle,
      iconBg: "bg-green-50",
      iconColor: "text-green-500",
      valColor: "text-green-600",
      topBorder: "border-t-green-400",
    },
    {
      label: "Pending",
      value: "25",
      icon: Clock,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-500",
      valColor: "text-orange-600",
      topBorder: "border-t-orange-400",
    },
    {
      label: "Exceptions",
      value: "3",
      icon: AlertTriangle,
      iconBg: "bg-red-50",
      iconColor: "text-red-500",
      valColor: "text-red-600",
      topBorder: "border-t-red-400",
    },
  ];

  const ROWS = [
    {
      n: "Acme Corp",
      gl: "10,000",
      c: "10,000",
      d: "0",
      s: "Matched",
      id: "BC-001",
    },
    {
      n: "Globex Inc",
      gl: "45,200",
      c: "40,000",
      d: "-5,200",
      s: "Variance",
      id: "BC-002",
    },
    {
      n: "Soylent Corp",
      gl: "2,500",
      c: "--",
      d: "--",
      s: "Pending",
      id: "BC-003",
    },
  ];

  const statusStyle = (s: string) => statusBadgeCss(s);

  const StatusIcon = ({ s }: { s: string }) =>
    s === "Matched" ? (
      <CheckCircle size={10} />
    ) : s === "Variance" ? (
      <AlertTriangle size={10} />
    ) : (
      <Clock size={10} />
    );

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-4">
        {STATS.map(
          ({
            label,
            value,
            icon: Icon,
            iconBg,
            iconColor,
            valColor,
            topBorder,
          }) => (
            <div
              key={label}
              className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-4 border-t-4 ${topBorder}`}
            >
              <div
                className={`${iconBg} ${iconColor} p-2.5 rounded-lg flex-shrink-0`}
              >
                <Icon size={18} />
              </div>
              <div>
                <div className={`text-2xl font-bold leading-none ${valColor}`}>
                  {value}
                </div>
                <div className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide mt-1">
                  {label}
                </div>
              </div>
            </div>
          ),
        )}
      </div>

      {/* Confirmation ledger — embedded table-list (no netbody-card/scroll-container) */}
      <div className="table-list" style={{ minHeight: 0 }}>
        <div className="table-list-scroll">
          <table className="table-list-table">
            <thead>
              <tr>
                <th className="table-list-th table-list-cell--sno">S.No</th>
                <th className="table-list-th">Vendor</th>
                <th className="table-list-th table-list-th--right">
                  GL Balance
                </th>
                <th className="table-list-th table-list-th--right">
                  Confirmed Bal.
                </th>
                <th className="table-list-th table-list-th--right">
                  Difference
                </th>
                <th className="table-list-th table-list-cell--status">Status</th>
                <th className="table-list-th table-list-th--right">Actions</th>
              </tr>
            </thead>
            <tbody aria-live="polite">
              {ROWS.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-list-empty-cell">
                    No confirmations recorded.
                  </td>
                </tr>
              )}
              {ROWS.map((row, i) => (
                <tr key={i} className="table-list-row">
                  <td className="table-list-cell table-list-cell--sno">
                    {i + 1}
                  </td>
                  <td className="table-list-cell">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 flex-shrink-0">
                        {row.n.charAt(0)}
                      </div>
                      <span className="table-list-cell-title">{row.n}</span>
                    </div>
                  </td>
                  <td className="table-list-cell table-list-cell--right">
                    <span className="">${row.gl}</span>
                  </td>
                  <td className="table-list-cell table-list-cell--right">
                    <span className="">
                      {row.c === "--" ? "—" : `$${row.c}`}
                    </span>
                  </td>
                  <td className="table-list-cell table-list-cell--right">
                    <span
                      className={`font-bold ${row.d.startsWith("-") ? "text-red-500" : row.d === "--" ? "text-gray-300" : "text-green-600"}`}
                    >
                      {row.d === "--" ? "—" : row.d}
                    </span>
                  </td>
                  <td className="table-list-cell">
                    <span
                      className={`inline-flex items-center gap-1 uppercase tracking-wide ${statusStyle(row.s)}`}
                    >
                      <StatusIcon s={row.s} />
                      {row.s}
                    </span>
                  </td>
                  <td className="table-list-cell table-list-cell--actions">
                    <ActionToolbar
                      itemId={row.id}
                      onAction={onAction}
                      compact
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// 10. Physical Inspection Module
const PI_LOCATIONS = [
  { key: "WH_A", label: "Warehouse A", counter: "J.Smith" },
  { key: "WH_B", label: "Warehouse B", counter: "R.Jones" },
  { key: "SHOW", label: "Showroom", counter: "M.Lee" },
  { key: "TRANS", label: "Transit", counter: "Unassigned" },
];

const PI_ASSETS: Record<
  string,
  { t: string; d: string; s: number; p: number; c: string; id: string }[]
> = {
  WH_A: [
    {
      t: "A-1001",
      d: "Dell Latitude 5420",
      s: 50,
      p: 50,
      c: "Good",
      id: "A1001",
    },
    {
      t: "A-1002",
      d: "Monitor 24 inch",
      s: 100,
      p: 98,
      c: "Good",
      id: "A1002",
    },
    {
      t: "A-1005",
      d: "Ergonomic Chairs",
      s: 20,
      p: 20,
      c: "Worn",
      id: "A1005",
    },
    { t: "A-1010", d: "Projector 4K", s: 2, p: 2, c: "Good", id: "A1010" },
  ],
  WH_B: [],
  SHOW: [],
  TRANS: [],
};

const PhysicalInspectionView = ({ setHeaderStatus, onAction }: any) => {
  const [activeLocation, setActiveLocation] = useState("WH_A");
  const activeItem = PI_LOCATIONS.find((l) => l.key === activeLocation)!;
  const assets = PI_ASSETS[activeLocation] || [];

  useEffect(() => {
    const total = Object.values(PI_ASSETS).flat().length;
    const matched = Object.values(PI_ASSETS)
      .flat()
      .filter((a) => a.s === a.p).length;
    const pct = total ? Math.round((matched / total) * 100) : 0;
    setHeaderStatus(
      <span className="status-badge xs success">
        <Check size={14} /> {pct}% Match
      </span>,
    );
  }, [setHeaderStatus, activeLocation]);

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left nav card */}
      <div className="netbody-card side-nav-card">
        <div className="side-nav-card-heading">Locations</div>
        <nav>
          {PI_LOCATIONS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveLocation(key)}
              className={`side-panel-menu-item${activeLocation === key ? " side-panel-menu-item--active" : ""}`}
            >
              <MapPin size={13} /> {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Right content card */}
      <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">
        {/* Mini header */}
        <div
          className="netbody-header"
          style={{ minHeight: "unset", padding: "8px 16px" }}
        >
          <div className="flex flex-col">
            <span className="text-sm font-bold text-gray-800">
              {activeItem.label}
            </span>
            <span className="text-[10px] text-gray-400">
              Counted by: {activeItem.counter}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="netbody-content">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Tag</th>
                    <th className="table-list-th">Description</th>
                    <th className="table-list-th table-list-th--right">Sys</th>
                    <th className="table-list-th table-list-th--right">
                      Phys (Count)
                    </th>
                    <th className="table-list-th">Condition</th>
                    <th className="table-list-th table-list-th--right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {assets.length === 0 && (
                    <tr>
                      <td colSpan={7} className="table-list-empty-cell">
                        No assets recorded for {activeItem.label}.
                      </td>
                    </tr>
                  )}
                  {assets.map((row, i) => (
                    <tr key={i} className="table-list-row">
                      <td className="table-list-cell table-list-cell--sno">
                        {i + 1}
                      </td>
                      <td className="table-list-cell">
                        <span className="text-xs text-gray-500">
                          {row.t}
                        </span>
                      </td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title">{row.d}</span>
                      </td>
                      <td className="table-list-cell table-list-cell--right text-gray-400">
                        {row.s}
                      </td>
                      <td
                        className="table-list-cell table-list-cell--right"
                        style={{ width: 110 }}
                      >
                        <FormControl.Input
                          type="number"
                          defaultValue={String(row.p)}
                          size="sm"
                        />
                      </td>
                      <td className="table-list-cell" style={{ width: 130 }}>
                        <FormControl.Select defaultValue={row.c} size="sm">
                          <option>Good</option>
                          <option>Damaged</option>
                          <option>Worn</option>
                        </FormControl.Select>
                      </td>
                      <td className="table-list-cell table-list-cell--actions">
                        <ActionToolbar
                          itemId={row.id}
                          onAction={onAction}
                          compact
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 11. Compliance Check Module
const ComplianceCheckView = ({ setHeaderStatus, onAction }: any) => {
  const [regs, setRegs] = useState([
    {
      id: 1,
      section: "GDPR 5.1(b)",
      req: "Purpose Limitation: Personal data collected for specified, explicit and legitimate purposes.",
      status: "Compliant",
      gap: "",
    },
    {
      id: 2,
      section: "GDPR 17",
      req: "Right to Erasure.",
      status: "Partial",
      gap: "Manual process exists, but no automated deletion logic.",
    },
    {
      id: 3,
      section: "GDPR 32",
      req: "Security of Processing: Implement technical measures.",
      status: "Non-Compliant",
      gap: "Encryption at rest not fully implemented.",
    },
  ]);

  const handleStatusChange = (id: any, newStatus: any) => {
    setRegs(regs.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
  };

  useEffect(() => {
    const compliant = regs.filter((r) => r.status === "Compliant").length;
    setHeaderStatus(
      <span
        className={`status-badge xs ${compliant === regs.length ? "success" : "warning"}`}
      >
        <Shield size={14} /> {compliant} / {regs.length} Compliant
      </span>,
    );
  }, [regs, setHeaderStatus]);

  return (
    <div className="space-y-3">
      {regs.map((reg) => (
        <div
          key={reg.id}
          className="bg-white rounded border border-gray-200 shadow-sm overflow-hidden flex flex-col"
        >
          <div className="p-3 flex items-start justify-between bg-gray-50 border-b border-gray-100">
            <div className="flex items-start gap-3">
              <span className="bg-white border text-gray-600 font-bold text-[10px] px-1.5 py-0.5 rounded flex-shrink-0">
                {" "}
                {reg.section}{" "}
              </span>
              <p className="text-sm font-medium text-gray-800 leading-tight">
                {" "}
                {reg.req}{" "}
              </p>
            </div>
            <ActionToolbar itemId={reg.id} onAction={onAction} compact />
          </div>
          <div className="p-3 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex gap-2 flex-shrink-0">
              {["Compliant", "Partial", "Non-Compliant"].map((st) => (
                <button
                  key={st}
                  onClick={() => handleStatusChange(reg.id, st)}
                  className={`px-2 py-1 rounded text-[10px] font-bold border transition-all ${
                    reg.status === st
                      ? st === "Compliant"
                        ? "bg-green-600 text-white border-green-600"
                        : st === "Partial"
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-red-600 text-white border-red-600"
                      : "bg-white text-gray-500 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
            <FormControl.Input
              type="text"
              placeholder="Gap Analysis / Notes..."
              defaultValue={reg.gap}
              size="sm"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// --- View Components (Defined Outside to Prevent Re-renders) ---

const ProcTypeIcon = ({ type, size = 20 }: { type: string; size?: number }) => {
  if (type === PROCEDURE_TYPES.CHECKLIST) return <CheckSquare size={size} />;
  if (type === PROCEDURE_TYPES.QUESTIONNAIRE) return <HelpCircle size={size} />;
  if (type === PROCEDURE_TYPES.PHYSICAL) return <MapPin size={size} />;
  if (type === PROCEDURE_TYPES.RATING) return <Scale size={size} />;
  if (type === PROCEDURE_TYPES.WALKTHROUGH)
    return <ArrowRightCircle size={size} />;
  if (type === PROCEDURE_TYPES.RISK_ASSESSMENT)
    return <AlertTriangle size={size} />;
  if (type === PROCEDURE_TYPES.CONTROL_TESTING) return <Shield size={size} />;
  if (type === PROCEDURE_TYPES.CONFIRMATIONS) return <Mail size={size} />;
  if (type === PROCEDURE_TYPES.FORM) return <FileText size={size} />;
  if (type === PROCEDURE_TYPES.COMPLIANCE) return <BookOpen size={size} />;
  return null;
};

const DashboardView = ({ procedures, handleProcedureClick }: any) => (
  <div className="h-full overflow-y-auto">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {procedures.map((proc: any) => (
        <div
          key={proc.id}
          onClick={() => handleProcedureClick(proc)}
          className="bg-white rounded-xl border border-gray-200 shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all group flex flex-col p-4 gap-3 min-h-[145px]"
          style={{ backgroundColor: "#ffffff" } as React.CSSProperties}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--brand-subtle)";
            e.currentTarget.style.borderColor = "var(--brand-light)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#ffffff";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }}
        >
          <div className="flex items-start justify-between">
            <div className="p-2 rounded-lg group-hover:text-white transition-colors flex-shrink-0" style={{
              backgroundColor: "var(--brand-subtle)",
              color: "var(--brand)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--brand-hover)";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--brand-subtle)";
              e.currentTarget.style.color = "var(--brand)";
            }}>
              <ProcTypeIcon type={proc.type} size={18} />
            </div>
            <span className="text-[10px] font-bold bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full border border-gray-200 tracking-wider">
              {proc.id}
            </span>
          </div>

          <div className="flex-1 flex flex-col gap-1">
            <h3 className="font-bold text-gray-800 text-sm leading-snug transition-colors" style={{ "--group-hover-color": "var(--brand)" } as React.CSSProperties}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "")}>
              {proc.type}
            </h3>
            <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
              {proc.name}
            </p>
          </div>

          <div className="flex items-center gap-1 text-[11px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--brand)" }}>
            View Interface <ArrowRightCircle size={12} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ProcedureMetadataHeader = ({ proc, headerStatus, onBack }: any) => {
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);

  const getStatusColor = (status: any) => {
    switch (status) {
      case "Initiated":
        return {
          backgroundColor: "var(--brand-subtle)",
          color: "var(--brand)",
          borderColor: "var(--brand-light)"
        };
      case "Submitted for Approval":
        return { backgroundColor: "#ede9fe", color: "#7c3aed", borderColor: "#e9d5ff" };
      case "Submitted for Review":
        return {
          backgroundColor: "var(--brand-subtle)",
          color: "var(--brand)",
          borderColor: "var(--brand-light)"
        };
      case "Closed":
        return { backgroundColor: "#dcfce7", color: "#16a34a", borderColor: "#86efac" };
      default:
        return { backgroundColor: "#f3f4f6", color: "#374151", borderColor: "#e5e7eb" };
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm z-20 sticky top-0">
      {/* ROW 1: Program | Name (centered) | Status */}
      <div className="px-6 py-3 flex items-center gap-4 border-b border-gray-50">
        {/* Left — Program */}
        <div className="flex-1 flex flex-col border-r border-gray-100 pr-4">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Briefcase size={11} className="text-gray-400" />
            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
              Audit Program
            </span>
          </div>
          <div
            className="text-xs font-semibold text-gray-700 truncate max-w-[220px]"
            title={proc.program}
          >
            {proc.program}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-[9px] text-gray-500">
            <span>
              Lead:{" "}
              <span className="font-medium text-gray-700">
                {proc.programLead}
              </span>
            </span>
            <span className="text-gray-300">•</span>
            <span>
              End:{" "}
              <span className="font-medium text-gray-700">
                {proc.programEnd}
              </span>
            </span>
          </div>
        </div>

        {/* Center — Procedure name + info flyout trigger */}
        <div className="flex flex-col items-center text-center shrink-0 relative">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-gray-900 leading-tight">
              {proc.name}
            </h2>
            <button
              onClick={() => setIsInfoExpanded(!isInfoExpanded)}
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              style={isInfoExpanded ? {
                backgroundColor: "var(--brand)",
                color: "white"
              } : {
                backgroundColor: "#f3f4f6",
                color: "#9ca3af"
              }}
              onMouseEnter={(e) => {
                if (!isInfoExpanded) {
                  e.currentTarget.style.backgroundColor = "var(--brand-subtle)";
                  e.currentTarget.style.color = "var(--brand)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isInfoExpanded) {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                  e.currentTarget.style.color = "#9ca3af";
                }
              }}
              title="Objective & Summary"
            >
              <BookOpen size={11} />
            </button>
          </div>

          {/* Flyout popover */}
          {isInfoExpanded && (
            <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-50 w-80 bg-white border border-gray-200 rounded-xl shadow-xl p-4 text-left">
              <div className="flex flex-col gap-3">
                <div>
                  <h5 className="font-bold text-gray-800 text-xs mb-1 flex items-center gap-1">
                    <BookOpen size={12} style={{ color: "var(--brand)" }} /> Objective
                  </h5>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {proc.objective}
                  </p>
                </div>
                <div className="h-px bg-gray-100" />
                <div>
                  <h5 className="font-bold text-gray-800 text-xs mb-1 flex items-center gap-1">
                    <ListTodo size={12} style={{ color: "var(--brand)" }} /> Summary
                  </h5>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {proc.summary || "No summary provided."}
                  </p>
                </div>
              </div>
              {/* Arrow */}
              <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />
            </div>
          )}
        </div>

        {/* Right — Status + Metric in one row */}
        <div className="flex-1 flex flex-row items-center justify-end gap-2 border-l border-gray-100 pl-4">
          <span
            className="px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wide"
            style={getStatusColor(proc.status) as React.CSSProperties}
          >
            {proc.status}
          </span>
          <div>{headerStatus}</div>
        </div>
      </div>

      {/* ROW 2: unified pill strip */}
      <div className="px-4 py-1.5 flex items-center justify-between bg-gray-50/60 border-b border-gray-100">
        {/* Left spacer */}
        <div className="flex-1" />

        {/* Center — single unified pill */}
        <div className="flex items-center bg-white border border-gray-200 rounded-full shadow-sm divide-x divide-gray-200 overflow-hidden flex-shrink-0">
          {/* Team */}
          <div className="flex items-center gap-2 px-3 py-1.5">
            <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wide">
              Team
            </span>
            <div className="flex items-center gap-2 text-[11px]">
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                  M
                </span>
                <span
                  className="font-medium text-gray-700 truncate max-w-[80px]"
                  title={proc.maker}
                >
                  {proc.maker}
                </span>
              </div>
              <span className="text-gray-300">|</span>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                  C
                </span>
                <span
                  className="font-medium text-gray-700 truncate max-w-[80px]"
                  title={proc.checker}
                >
                  {proc.checker}
                </span>
              </div>
            </div>
          </div>

          {/* Period */}
          <div className="flex items-center gap-2 px-3 py-1.5">
            <Calendar size={10} className="text-gray-400 flex-shrink-0" />
            <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wide">
              Period
            </span>
            <span className="text-[11px] font-medium text-gray-700 whitespace-nowrap">
              {proc.rolloutDate} <span className="text-gray-300 mx-0.5">→</span>{" "}
              {proc.dueDate}
            </span>
          </div>

          {/* Population / Sample */}
          <div className="flex items-center gap-3 px-3 py-1.5">
            <div className="flex flex-col">
              <span className="text-[8px] text-gray-400 uppercase font-bold tracking-wide leading-none mb-0.5">
                Population
              </span>
              <span
                className="text-[11px] font-medium text-gray-700 truncate max-w-[130px]"
                title={proc.population}
              >
                {proc.population || "N/A"}
              </span>
            </div>
            <div className="w-px h-5 bg-gray-200" />
            <div className="flex flex-col">
              <span className="text-[8px] text-gray-400 uppercase font-bold tracking-wide leading-none mb-0.5">
                Sample
              </span>
              <span
                className="text-[11px] font-bold text-orange-600 truncate max-w-[130px]"
                title={proc.sampleSize}
              >
                {proc.sampleSize || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Right — Back button */}
        <div className="flex-1 flex justify-end">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-[11px] font-bold text-gray-500 uppercase tracking-wide hover:border-orange-400 hover:text-orange-600 transition-all shadow-sm"
            >
              <ArrowLeft size={12} /> Back to Procedures
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const WorkpaperView = ({
  selectedProcedure,
  setActiveView,
  headerStatus,
  setHeaderStatus,
  onAction,
  onBack,
}: any) => {
  const renderModule = () => {
    const props = { setHeaderStatus, onAction };
    switch (selectedProcedure.type) {
      case PROCEDURE_TYPES.CHECKLIST:
        return <ChecklistView {...props} />;
      case PROCEDURE_TYPES.QUESTIONNAIRE:
        return <QuestionnaireView {...props} />;
      case PROCEDURE_TYPES.FORM:
        return <FormView {...props} />;
      case PROCEDURE_TYPES.RATING:
        return <RatingSystemView {...props} />;
      case PROCEDURE_TYPES.WALKTHROUGH:
        return <WalkthroughView {...props} />;
      case PROCEDURE_TYPES.RISK_ASSESSMENT:
        return <RiskAssessmentView {...props} />;
      case PROCEDURE_TYPES.CONTROL_TESTING:
        return <ControlTestingView {...props} />;
      case PROCEDURE_TYPES.CONFIRMATIONS:
        return <BalanceConfirmationsView {...props} />;
      case PROCEDURE_TYPES.PHYSICAL:
        return <PhysicalInspectionView {...props} />;
      case PROCEDURE_TYPES.COMPLIANCE:
        return <ComplianceCheckView {...props} />;
      default:
        return (
          <div className="p-10 text-center text-gray-400">
            {" "}
            Unknown Procedure Type{" "}
          </div>
        );
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* New 3-Row Metadata Header */}
      <ProcedureMetadataHeader
        proc={selectedProcedure}
        headerStatus={headerStatus}
        onBack={onBack}
      />

      {/* Workpaper Canvas — each module owns its own card/scroll structure */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {renderModule()}
      </div>
    </div>
  );
};

// --- Main Application Component ---

export default function AuditorProceduresContent() {
  const [currentRole, setCurrentRole] = useState(ROLES.IN_CHARGE);
  const [activeView, setActiveView] = useState("Dashboard");
  const [selectedProcedure, setSelectedProcedure] = useState<any>(null);
  const [procedures, setProcedures] = useState(INITIAL_PROCEDURES);

  // Header Status State for Portal-like behavior
  const [headerStatus, setHeaderStatus] = useState<any>(null);

  // Toast State
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Action Modal State
  const [activeAction, setActiveAction] = useState<any>(null); // {type: 'evidence' | 'approval' | ..., itemId: string }

  // --- Helpers ---
  const handleProcedureClick = (proc: any) => {
    setSelectedProcedure(proc);
    setHeaderStatus(null); // Reset header
    setActiveView("Workpaper");
  };

  const handleActionClick = (type: any, itemId: any) => {
    setActiveAction({ type, itemId });
  };

  return (
    <div className="netbody-card flex flex-col">
      <div className="netbody-content flex flex-col flex-1 min-h-0">
        <div className="scroll-container-nofilters overflow-y-auto py-2">
          {activeView === "Dashboard" && (
            <DashboardView
              procedures={procedures}
              handleProcedureClick={handleProcedureClick}
            />
          )}
          {activeView === "Workpaper" && (
            <WorkpaperView
              selectedProcedure={selectedProcedure}
              setActiveView={setActiveView}
              headerStatus={headerStatus}
              setHeaderStatus={setHeaderStatus}
              onAction={handleActionClick}
              onBack={() => setActiveView("Dashboard")}
            />
          )}
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Global Action Modal */}
      {activeAction && (
        <UniversalActionModal
          isOpen={!!activeAction}
          type={activeAction.type}
          itemId={activeAction.itemId}
          onClose={() => setActiveAction(null)}
        />
      )}
    </div>
  );
}
